import {
  useFindMlsListingByAddressLazyQuery,
  useFindUserCapturedPropertyDetailsByAddressLazyQuery,
  useSearchMlsListingsWithFiltersLazyQuery,
} from '@client/graphql/__generated__/main-operations';
import {
  MlsListingFiltersInput,
  MlsListingMlsFragment,
  MlsListingStatus,
  PropertyDetailsFragment,
  UsState,
} from '@client/graphql/__generated__/types';
import { GeocodingFeature } from '@mapbox/search-js-core';
import { isEmpty, isNil } from 'lodash';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { MapRef } from 'react-map-gl';
import {
  ADDRESS_FEATURE_TYPES,
  MlsListingWithListing,
} from '../ExploreContext';
import {
  getAddressInputFromSearchResult,
  getMatchingListingForSearchResult,
  SearchResult,
} from '../helpers';

export const useExploreContextQueries = ({
  showClosedStatus = false,
  skipMlsListingsSearch = false,
  // mapRef,
}: {
  showClosedStatus?: boolean;
  skipMlsListingsSearch?: boolean;
  mapRef: RefObject<MapRef>;
}) => {
  // Used for if we need to wait for data to load before opening the popup
  const [shouldOpenPopup, setShouldOpenPopup] = useState(false);
  const [searchResult, setSearchResult] = useState<GeocodingFeature | null>(
    null
  );
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<MlsListingFiltersInput>({});
  const [listings, setListings] = useState<MlsListingWithListing[]>([]);
  const [foundMLSListing, setFoundMLSListing] =
    useState<MlsListingWithListing | null>(null);
  const [selectedListing, setSelectedListing] = useState<
    MlsListingWithListing[]
  >([]);
  const [shouldPromptForOffMarketListing, setShouldPromptForOffMarketListing] =
    useState(false);
  const [foundPropertyDetails, setFoundPropertyDetails] =
    useState<PropertyDetailsFragment | null>(null);

  const [searchMlsListingsWithFilters, { loading }] =
    useSearchMlsListingsWithFiltersLazyQuery();
  const [findMlsListingByAddress, { loading: findMlsListingByAddressLoading }] =
    useFindMlsListingByAddressLazyQuery();
  const [
    findPropertyDetailsByAddress,
    { loading: findPropertyDetailsByAddressLoading },
  ] = useFindUserCapturedPropertyDetailsByAddressLazyQuery();

  const updateFilters = useCallback(
    (filters: Partial<MlsListingFiltersInput>) => {
      setFilters((prev) => ({
        ...prev,
        ...filters,
      }));
    },
    [setFilters]
  );

  const clearFilters = () => {
    setFilters((prev) => ({
      bbox: prev.bbox,
    }));
  };

  const findMLSListing = async (result?: GeocodingFeature) => {
    if (!result) {
      return undefined;
    }

    const { streetAddress, city, state, zipCode, unit } =
      getAddressInputFromSearchResult(result);

    const { data } = await findMlsListingByAddress({
      variables: {
        input: {
          addressInput: {
            streetAddress,
            city,
            state: state as UsState,
            zipCode,
            unit: unit,
          },
          onlyLiveStatuses: !showClosedStatus,
        },
      },
    });

    if (data?.findMLSListingByAddress) {
      return data?.findMLSListingByAddress;
    }

    if (
      !data?.findMLSListingByAddress &&
      streetAddress !== result?.properties?.name
    ) {
      // Fallback search using the property name when address lookup fails
      const { data: secondarySearchData } = await findMlsListingByAddress({
        variables: {
          input: {
            addressInput: {
              streetAddress: result?.properties?.name,
              city,
              state: state as UsState,
              zipCode,
              unit: unit,
            },
            onlyLiveStatuses: !showClosedStatus,
          },
        },
      });

      return secondarySearchData?.findMLSListingByAddress;
    }

    return undefined;
  };

  const findPropertyDetails = async (result?: GeocodingFeature) => {
    if (!result) {
      return undefined;
    }

    const { streetAddress, city, state, zipCode, unit } =
      getAddressInputFromSearchResult(result);

    const { data } = await findPropertyDetailsByAddress({
      variables: {
        input: {
          addressInput: {
            streetAddress,
            city,
            state: state as UsState,
            zipCode,
            unit,
          },
        },
      },
    });

    if (data?.findUserCapturedPropertyDetailsByAddress) {
      return data?.findUserCapturedPropertyDetailsByAddress;
    }

    if (
      !data?.findUserCapturedPropertyDetailsByAddress &&
      streetAddress !== result?.properties?.name
    ) {
      const { data: secondarySearchData } = await findPropertyDetailsByAddress({
        variables: {
          input: {
            addressInput: {
              streetAddress: result?.properties?.name,
              city,
              state: state as UsState,
              zipCode,
              unit,
            },
          },
        },
      });

      return secondarySearchData?.findUserCapturedPropertyDetailsByAddress;
    }

    return undefined;
  };

  const hasFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'premiumOnly' || key === 'bbox') {
      return false;
    }

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return (
        !isEmpty(value) && Object.values(value).some((value) => !isNil(value))
      );
    }

    return !isNil(value);
  });

  useEffect(() => {
    void (async () => {
      if (skipMlsListingsSearch) {
        return;
      }

      const { data } = await searchMlsListingsWithFilters({
        variables: {
          input: {
            filters: {
              ...filters,
              ...(showClosedStatus && !filters?.statuses?.length
                ? {
                    statuses: [
                      MlsListingStatus.Active,
                      MlsListingStatus.ActiveUnderContract,
                      MlsListingStatus.Pending,
                      MlsListingStatus.ComingSoon,
                      MlsListingStatus.Closed,
                    ],
                  }
                : {}),
            },
            limit: 200,
            // TODO: Add back this logic when we have a better way to group same addresses with different units
            // (mapRef.current?.getZoom() || DEFAULT_POSITION.zoom) >
            // ZOOM_LEVEL_MARKER_CHANGE
            //   ? 60
            //   : 200,
          },
        },
      });

      const mlsListings = data?.searchMlsListingsWithFilters?.mlsListings || [];
      setListings(mlsListings);
      setTotalCount(data?.searchMlsListingsWithFilters?.totalCount || 0);

      if (
        shouldOpenPopup &&
        ADDRESS_FEATURE_TYPES.includes(
          searchResult?.properties?.feature_type || ''
        )
      ) {
        const matchingListing = getMatchingListingForSearchResult(
          mlsListings,
          searchResult as SearchResult
        );
        if (matchingListing) {
          setSelectedListing([matchingListing]);
        } else if (foundMLSListing) {
          const existingListing = mlsListings.find(
            (mlsListing) =>
              mlsListing.mlsListing.id === foundMLSListing.mlsListing.id
          );
          if (existingListing) {
            setFoundMLSListing(null);
          }
          setSelectedListing(
            existingListing || foundMLSListing
              ? [existingListing || foundMLSListing]
              : []
          );
        } else {
          if (searchResult) {
            // Look for off market listing if cant find a matching mls listing
            const foundPropertyDetails =
              await findPropertyDetails(searchResult);
            if (foundPropertyDetails) {
              setFoundPropertyDetails(foundPropertyDetails);
            } else {
              setShouldPromptForOffMarketListing(true);
              setFoundPropertyDetails(null);
            }
          }
          setSelectedListing([]);
        }

        // Resets the popup listing if the property is no longer in the filtered results
      } else if (selectedListing?.length) {
        if (
          ![...mlsListings, ...(foundMLSListing ? [foundMLSListing] : [])].some(
            (mlsListing) =>
              selectedListing.some(
                (selected) =>
                  selected.mlsListing.id === mlsListing.mlsListing.id
              )
          )
        ) {
          setSelectedListing([]);
        }
      }
      setShouldOpenPopup(false);
    })();
    // Don't need to run this when search Result changes. Only when the filters change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchMlsListingsWithFilters]);

  const handleRetrieve = async (result: GeocodingFeature) => {
    const matchingListing = getMatchingListingForSearchResult(
      listings,
      result as SearchResult
    );
    if (matchingListing) {
      setSelectedListing([matchingListing]);
      setFoundMLSListing(null);
    } else {
      if (
        ADDRESS_FEATURE_TYPES.includes(result?.properties?.feature_type || '')
      ) {
        const foundMlsListing = await findMLSListing(result);
        if (foundMlsListing) {
          setFoundMLSListing({ mlsListing: foundMlsListing });
        } else {
          setFoundMLSListing(null);
        }
      }
      setShouldOpenPopup(true);
    }
    setShouldPromptForOffMarketListing(false);
    setSearchResult(result);
  };

  const disclaimers = useMemo(() => {
    return Object.values(
      listings?.reduce((acc, listing) => {
        const originatingSystemName =
          listing.mlsListing.mls?.originatingSystemName;
        if (originatingSystemName && !acc[originatingSystemName]) {
          acc[originatingSystemName] = listing.mlsListing.mls;
        }

        return acc;
      }, {}) || {}
    ).filter(Boolean) as MlsListingMlsFragment[];
  }, [listings]);

  return {
    listings,
    totalCount,
    filters,
    updateFilters,
    clearFilters,
    shouldOpenPopup,
    setShouldOpenPopup,
    shouldPromptForOffMarketListing,
    setShouldPromptForOffMarketListing,
    selectedListing,
    setSelectedListing,
    foundMLSListing,
    loading:
      findMlsListingByAddressLoading ||
      loading ||
      findPropertyDetailsByAddressLoading,
    hasFilters,
    setTotalCount,
    foundPropertyDetails,
    setFoundPropertyDetails,
    handleRetrieve,
    searchResult,
    setSearchResult,
    disclaimers,
  };
};
